import { useRef, useEffect, useCallback } from 'react';
import { RingSoundOption } from './useLocalSettings';

// ============================================================================
// USE RING SOUND HOOK - ZİL SESİ YÖNETİMİ (Web Audio API)
// ============================================================================

export interface UseRingSoundOptions {
  /** Zil sesi açık mı? */
  enabled: boolean;
  /** Seçili zil sesi değeri */
  selectedSound: string;
  /** Zil sesi seçenekleri */
  soundOptions: RingSoundOption[];
}

export interface UseRingSoundReturn {
  /** Zil sesini çal */
  playRingSound: () => void;
  /** Zil sesini durdur */
  stopRingSound: () => void;
}

/**
 * Web Audio API ile zil sesi çalma/durdurma hook'u
 */
export const useRingSound = (options: UseRingSoundOptions): UseRingSoundReturn => {
  const { enabled, selectedSound, soundOptions } = options;
  
  // Web Audio API ref'leri
  const audioContextRef = useRef<AudioContext | null>(null);
  const oscillatorRef = useRef<OscillatorNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const ringIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  /**
   * Zil sesini durdur
   */
  const stopRingSound = useCallback(() => {
    console.log('📞 [RingSound] Zil sesi durduruluyor');
    
    // Interval'ı temizle
    if (ringIntervalRef.current) {
      clearTimeout(ringIntervalRef.current);
      ringIntervalRef.current = null;
    }
    
    // Oscillator'ı durdur
    if (oscillatorRef.current) {
      try {
        oscillatorRef.current.stop();
      } catch (e) {
        // Zaten durmuş olabilir
      }
      oscillatorRef.current = null;
    }
    
    // AudioContext'i kapat
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch (e) {
        // Zaten kapalı olabilir
      }
      audioContextRef.current = null;
    }
    
    gainNodeRef.current = null;
  }, []);
  
  /**
   * Zil sesini çal
   */
  const playRingSound = useCallback(() => {
    if (!enabled) {
      console.log('📞 [RingSound] Zil sesi kapalı');
      return;
    }
    
    console.log('📞 [RingSound] Zil sesi çalınıyor:', selectedSound);
    
    // Önceki sesi durdur
    stopRingSound();
    
    try {
      // AudioContext oluştur
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextClass();
      audioContextRef.current = audioContext;
      
      // Seçili zil sesinin ayarlarını al
      const soundConfig = soundOptions.find(s => s.value === selectedSound) || soundOptions[0];
      
      let patternIndex = 0;
      
      const playTone = () => {
        if (!audioContextRef.current) return;
        
        const currentAudioContext = audioContextRef.current;
        
        // Klasik telefon için özel ses (gerçek telefon çalma sesi)
        if (soundConfig.value === 'ring1') {
          // Ana oscillator (425 Hz - Türkiye telefon standardı)
          const oscillator = currentAudioContext.createOscillator();
          const gainNode = currentAudioContext.createGain();
          
          // Modülasyon için LFO (Low Frequency Oscillator) - zil titreşimi efekti
          const lfo = currentAudioContext.createOscillator();
          const lfoGain = currentAudioContext.createGain();
          
          oscillator.type = 'sine';
          oscillator.frequency.setValueAtTime(425, currentAudioContext.currentTime);
          
          // LFO ayarları - 20Hz modülasyon (zil titreşimi)
          lfo.type = 'sine';
          lfo.frequency.setValueAtTime(20, currentAudioContext.currentTime);
          lfoGain.gain.setValueAtTime(0.3, currentAudioContext.currentTime);
          
          // Bağlantılar
          lfo.connect(lfoGain);
          lfoGain.connect(gainNode.gain);
          oscillator.connect(gainNode);
          gainNode.connect(currentAudioContext.destination);
          
          gainNode.gain.setValueAtTime(0.4, currentAudioContext.currentTime);
          
          oscillator.start();
          lfo.start();
          
          oscillatorRef.current = oscillator;
          gainNodeRef.current = gainNode;
          
          // 1 saniye çal, sonra dur
          const duration = soundConfig.pattern[0]; // 1000ms
          
          setTimeout(() => {
            try {
              oscillator.stop();
              lfo.stop();
            } catch (e) { /* zaten durmuş */ }
            oscillatorRef.current = null;
            
            // Sessiz bekle ve tekrar çal
            if (audioContextRef.current) {
              const silenceDuration = soundConfig.pattern[1]; // 2000ms
              ringIntervalRef.current = setTimeout(playTone, silenceDuration);
            }
          }, duration);
          
        } else {
          // Diğer zil sesleri için basit oscillator
          const oscillator = currentAudioContext.createOscillator();
          const gainNode = currentAudioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(currentAudioContext.destination);
          
          oscillator.type = soundConfig.value === 'ring4' ? 'square' : 'sine';
          oscillator.frequency.setValueAtTime(soundConfig.frequency, currentAudioContext.currentTime);
          
          gainNode.gain.setValueAtTime(0.3, currentAudioContext.currentTime);
          
          oscillator.start();
          oscillatorRef.current = oscillator;
          gainNodeRef.current = gainNode;
          
          // Pattern'e göre ses dur/başla
          const duration = soundConfig.pattern[patternIndex % soundConfig.pattern.length];
          
          setTimeout(() => {
            try {
              if (oscillatorRef.current) {
                oscillatorRef.current.stop();
              }
            } catch (e) { /* zaten durmuş */ }
            oscillatorRef.current = null;
            patternIndex++;
            
            // Sessiz bekle ve tekrar çal
            if (audioContextRef.current) {
              const silenceDuration = soundConfig.pattern[patternIndex % soundConfig.pattern.length];
              ringIntervalRef.current = setTimeout(playTone, silenceDuration);
              patternIndex++;
            }
          }, duration);
        }
      };
      
      // İlk tonu çal
      playTone();
      
      console.log('📞 [RingSound] Zil sesi başlatıldı');
      
    } catch (err) {
      console.error('📞 [RingSound] Ses çalınamadı:', err);
    }
  }, [enabled, selectedSound, soundOptions, stopRingSound]);
  
  // Component unmount olduğunda sesi durdur
  useEffect(() => {
    return () => {
      stopRingSound();
    };
  }, [stopRingSound]);
  
  return {
    playRingSound,
    stopRingSound,
  };
};

export default useRingSound;
